/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { useCallback, useEffect, useState } from 'react'

import { Tooltip } from 'antd'
import OptionButtonIcon from 'src/components/List/OptionButtons/Icon'
import { Flex } from '../../components/Layout'
import List from '../../components/List'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import MoreOptions, {
  MoreOptionListItem,
} from '../../components/List/OptionButtons/More'
import { useSelector } from 'src/hooks/useRedux'
import useGetListItems from '../../components/List/useGetListItems'
import LoadingSpinner from '../../components/LoadingSpinner'
import Picture from '../../components/Picture'

const OrgCell = ({ item }) => (
  <Flex alignItems='center'>
    <Picture
      size='kxsmall'
      square
      fit
      imageId={getImageThumbnailId(item)}
      iconName='users'
      css={css`
        margin-right: 8px;
      `}
    />
    {item?.name || item?.name_full}
  </Flex>
)

/**
 * Custom favorite button component that handles local state when isFavorites is true
 * @param {Object} props - Component props
 * @param {string} props.entityType - Type of entity
 * @param {Object} props.item - The item object
 * @param {boolean} props.isFavourite - Initial favorite state
 * @param {boolean} props.isFavoritesView - Whether we're in favorites view
 * @param {Function} props.onFavoriteChange - Callback when favorite state changes
 * @returns {JSX.Element} Favorite button component
 */
const CustomOptionButtonFavourite = ({
  entityType,
  item,
  isFavourite,
  isFavoritesView,
  onFavoriteChange,
}) => {
  const [loading, setLoading] = useState(false)
  const [isFav, setIsFav] = useState(isFavourite)

  // Update local state when prop changes
  useEffect(() => {
    setIsFav(isFavourite)
  }, [isFavourite])

  const entityName = useCallback(() => {
    switch (entityType) {
      case ENTITY_TYPES.org:
        return ' organization'
      case ENTITY_TYPES.team:
        return ' team'
      case ENTITY_TYPES.schedule:
        return ' competition'
      case ENTITY_TYPES.game:
        return ' game'
      default:
        return ''
    }
  }, [entityType])

  const onClick = async () => {
    setLoading(true)
    try {
      if (isFavoritesView) {
        // In favorites view, just update local state without API call
        const newState = !isFav
        setIsFav(newState)
        onFavoriteChange(newState)
      } else {
        // Normal behavior - make API call
        await req(`/favorites/${entityType}/${item.id}`, {
          method: isFav ? 'DELETE' : 'POST',
        })
        setIsFav(!isFav)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Flex noFlex alignItems='center' justifyContent='center'>
      {loading ? (
        <div style={{ width: 18 }}>
          <LoadingSpinner size={1.3} borderSize={1.8} />
        </div>
      ) : (
        <div
          css={css`
            position: relative;
            display: inline-block;
          `}
        >
          <Tooltip
            title={`${isFav ? 'Unfollow' : 'Follow'}${entityName()}`}
            mouseEnterDelay={1}
            placement='bottom'
          >
            <OptionButtonIcon
              faType={isFav ? 'fas' : 'far'}
              name='star'
              onClick={onClick}
            />
          </Tooltip>
        </div>
      )}
    </Flex>
  )
}

const OrgList = ({
  refreshKey,
  setCount = () => {},
  slug,
  setInitialState,
  onEditOrg,
  onClickWebPluginOnScheduleList = () => {},
  isFavorites = false,
}) => {
  const accountRootOrgId = useSelector(
    (state) => state.account?._meAccount?.organization?.id ?? null
  )
  // Local state for managing favorites when isFavorites is true
  const [localFavorites, setLocalFavorites] = useState(new Set())

  const listProps = useGetListItems(slug, {
    onComplete: (response) => setCount(response.meta.pagination.count),
  })

  const {
    sort,
    direction,
    getItems,
    permissions,
    debouncedSearchTerm,
    features,
    items,
  } = listProps

  // Initialize local favorites when items change and we're in favorites view
  useEffect(() => {
    if (isFavorites && items) {
      // Set all items as favorites initially when in favorites view
      const favoriteIds = new Set(items.map((item) => item.id))
      setLocalFavorites(favoriteIds)
    }
  }, [items, isFavorites])

  /**
   * Handle favorite state change for local management
   * @param {string|number} itemId - ID of the item
   * @param {boolean} newFavoriteState - New favorite state
   */
  const handleFavoriteChange = useCallback(
    (itemId, newFavoriteState) => {
      if (isFavorites) {
        setLocalFavorites((prev) => {
          const newSet = new Set(prev)
          if (newFavoriteState) {
            newSet.add(itemId)
          } else {
            newSet.delete(itemId)
          }
          return newSet
        })
      }
    },
    [isFavorites]
  )

  useEffect(() => {
    getItems()
  }, [refreshKey, sort, direction, debouncedSearchTerm])

  const headers = [
    {
      text: t('common:name'),
      sizePercent: 37,
      sort: 'name',
      Cell: OrgCell,
      to: (item) => `${ROUTES.ORG_ROOT}/${item.id}`,
    },
    {
      text: t('Web:subOrg_plural', { count: 2 }),
      sizePercent: 12,
      minWidth: 90,
      sort: 'children_count',
      accessor: 'children_count',
    },
    {
      text: t('common:competition_plural', { count: 2 }),
      sizePercent: 14,
      minWidth: 120,
      sort: 'schedules_count',
      accessor: 'schedules_count',
    },
    {
      text: t('common:team_plural', { count: 2 }),
      sizePercent: 10,
      sort: 'teams_count',
      accessor: 'teams_count',
    },
  ]

  const renderGeneratePluginTag = ({ onClick }) => {
    return (
      <MoreOptionListItem
        onClick={() => {
          onClick()
        }}
        iconName='plug'
        faType='far'
        title={'Generate JS Plugin Tag'}
      />
    )
  }

  const options = (item) => {
    const permission = permissions[item.id]

    // Determine favorite state based on view type
    const favoriteState = isFavorites
      ? localFavorites.has(item.id)
      : item.is_favorite
    const moreOptions = []
    if (permission.update) {
      moreOptions.push({
        key: 'plugin',
        Component: renderGeneratePluginTag,
        componentProps: {
          Button: renderGeneratePluginTag,
          schedule: item,
          scheduleId: item.id,
          onClick: () => {
            onClickWebPluginOnScheduleList(item)
          },
        },
      })
    }
    // The account root organization is not deletable. Demo orgs stay
    // deletable. List rows often omit parent_id, so match the account id.
    const isAccountRootOrganization =
      accountRootOrgId != null && item.id === accountRootOrgId && !item.is_demo
    if (
      permission.delete &&
      !isAccountRootOrganization &&
      (item.parent_id !== null || item.is_demo)
    ) {
      moreOptions.push({
        key: 'delete',
        Component: OptionButtonDelete,
        componentProps: {
          useForceDelete: true,
          unmountOnClose: true,
          entityType: ENTITY_TYPES.org,
          itemId: item.id,
          title: `Delete ${item.name_full}`,
          type: t('common:organization'),
          iconName: 'trash',
          // onComplete: getItems,
          // We need to do a complete refresh of the screen so we have
          // the empty state correctly
          onComplete: () => {
            window.location.reload()
          },
        },
      })
    }
    const opts = [
      permission.update && (
        <OptionButtonIcon
          name='pencil'
          onClick={() => {
            setInitialState(item)
            onEditOrg()
          }}
        />
      ),
      <CustomOptionButtonFavourite
        key='fav'
        entityType={ENTITY_TYPES.org}
        item={item}
        isFavourite={favoriteState}
        isFavoritesView={isFavorites}
        onFavoriteChange={(newState) => handleFavoriteChange(item.id, newState)}
      />,
      // We should only allow to delete if this is not a root org
      // (a root org is an org with parent_id === null)
      <MoreOptions key='more' options={moreOptions} />,
    ]
    return opts.filter((o) => o !== undefined)
  }

  return (
    <List
      headers={headers.filter((o) => o !== undefined)}
      options={options}
      listProps={listProps}
      noItemsText={t('common:organization_plural', { count: 2 })}
      showSearch
    />
  )
}

export default OrgList
